import { Component } from '@angular/core';
import {
  ItemDetailStatusEnum,
  ItemReplacement,
  ItemReplacementStatus,
  ItemReplacementType,
  PoDetail,
  PoHeader,
} from 'app/model/entities/entity-model';
import { ItemDetail } from 'app/model/entities/item-detail';
import { ItemDetailStatusCodes } from 'app/model/enums/item-detail-status-code';
import { ItemReplacementStatusCode } from 'app/model/enums/item-replacement-status-code';
import { ItemReplacementTypeCode } from 'app/model/enums/item-replacement-type-code';
import { PoStatusCode } from 'app/model/enums/po-status-code';
import { DialogBaseComponent, DialogParams } from 'app/shared/dialog-base.component';
import { DomainService } from 'app/shared/domain.service';
import _ from 'lodash';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

export interface InventoryDamageDialogParams extends DialogParams {
  itemDetail: ItemDetail;
}

@Component({
  templateUrl: './inventory-damage-dialog.component.html',
  standalone: false,
})
export class InventoryDamageDialogComponent extends DialogBaseComponent<InventoryDamageDialogParams> {
  itemDetail!: ItemDetail;
  itemReplacementTypes!: ItemReplacementType[];
  itemReplacementStatuses!: ItemReplacementStatus[];
  itemReplacementType!: ItemReplacementType;
  itemReplacementStatus!: ItemReplacementStatus;
  addonBinId!: number;

  constructor(
    public config: DynamicDialogConfig<InventoryDamageDialogParams>,
    public dialogRef: DynamicDialogRef,
    public domainService: DomainService,
  ) {
    super(config, dialogRef, domainService);
    Object.assign(this.config, {
      width: '800px',
      height: '500px',
    });
    this.prepare();
  }

  async prepare() {
    this.itemDetail = this.params.itemDetail;
    const itr = this.itemDetail.itemReplacement;
    if (itr != null) {
      this.itemReplacementType = itr.itemReplacementType;
      this.itemReplacementStatus = itr.itemReplacementStatus;
    }
    this.itemReplacementTypes = await this.dbQueryService.cacheAndGetAll(ItemReplacementType);
    this.itemReplacementStatuses = await this.dbQueryService.cacheAndGetAll(ItemReplacementStatus);
    if (ItemDetailStatusCodes.InProcess.includes(this.itemDetail.itemDetailStatusId)) {
      this.config.header = 'Mark item Damaged/Lost/Other';
    } else {
      // Wrong item replacement types are only available for in-process itemDetails.
      this.itemReplacementTypes = this.itemReplacementTypes.filter((x) => !x.id.startsWith('WRONG'));
      this.config.header = 'Inventory Damaged/Lost Items';
    }

    this.isPageReady = true;
  }

  onItemReplacementTypeChange(irt: ItemReplacementType) {
    this.itemReplacementType = irt;
    this.itemReplacementStatus = this.itemReplacementStatuses.find((irs) => irs.id === ItemReplacementStatusCode.None)!;
  }

  async apply() {
    const summary: string[] = [];
    let itr = this.itemDetail.itemReplacement;
    const reasonName = this.itemReplacementType.name.toLowerCase();

    if (ItemDetailStatusCodes.Addon.includes(this.itemDetail.itemDetailStatusId)) {
      this.itemDetail.itemDetailAddons.slice().forEach((itda) => {
        this.addonBinId = <any>itda.addonBinId;
        itda.entityAspect.setDeleted();
      });
      summary.push(`Item <b>${this.itemDetail.id}</b> removed from addon bin <b>${this.addonBinId}</b>.`);
    }

    if (([ItemReplacementTypeCode.WrongByManuf, ItemReplacementTypeCode.WrongItem] as string[]).includes(this.itemReplacementType.id)) {
      this.itemDetail.itemDetailStatusId = ItemDetailStatusEnum.InInventory;
      this.itemDetail.joDetailId = null as any;
      summary.push(`Item status set to <b>In Inventory</b> (unassigned from job order).`);
    } else {
      this.itemDetail.itemDetailStatusId = ItemDetailStatusEnum.DamagedOrOther;
      summary.push(`Item status set to <b>Damaged/Other</b>.`);
    }

    if (itr == null) {
      const struct = <ItemReplacement>{};
      struct.itemDetail = this.itemDetail;
      struct.itemReplacementStatus = this.itemReplacementStatus;
      struct.itemReplacementType = this.itemReplacementType;
      struct.crtnTs = new Date();
      struct.crtnUserInit = this.authService.getUser().initials;
      itr = this.uow.createEntity(ItemReplacement, struct);
      summary.push(`Item replacement record created (reason: <b>${reasonName}</b>).`);
    } else {
      itr.itemReplacementStatus = this.itemReplacementStatus;
      itr.itemReplacementType = this.itemReplacementType;
      summary.push(`Item replacement record updated (reason: <b>${reasonName}</b>).`);
    }

    if (this.itemDetail.joDetail != null) {
      await this.createOrFindAndUpdatePuchaseOrder(summary);
    }

    await this.dbSaveService.saveChanges();

    // check if the addonBin associated with this item is empty - if so delete it.
    if (this.addonBinId != null) {
      const addonBin = await this.dbQueryService.getAddonBin(this.addonBinId);
      if (addonBin!.itemDetailAddons.length == 0) {
        addonBin!.entityAspect.setDeleted();
        await this.dbSaveService.saveChanges();
        summary.push(`Addon bin <b>${this.addonBinId}</b> was empty and has been deleted.`);
      }
    }

    await this.dialogService.showOkMessage('Mark Damaged - Complete', summary.join('<br><br>'));
    this.dialogRef.close(true);
  }

  async createOrFindAndUpdatePuchaseOrder(summary: string[]) {
    const poHeaders = await this.dbQueryService.getUnpostedPoHeadersForManufacturer(this.itemDetail.product.productType.manufacturer.id);
    const now = new Date();
    const manuf = this.itemDetail.product.productType.manufacturer;
    let poHeader = _.first(poHeaders);
    let isNewPo = false;
    if (poHeader == null) {
      const poStruct = {
        shippingWarehouseId: 1, // TODO: magic number for unitec
        manufacturerId: manuf.id,
        poStatusId: PoStatusCode.NewUnposted,
        crtnTs: now,
      };
      poHeader = this.uow.createEntity(PoHeader, poStruct);
      isNewPo = true;
    }
    const podStruct = {} as PoDetail;
    const jod = this.itemDetail.joDetail;
    podStruct.poHeaderId = poHeader.id;
    podStruct.joDetailId = jod.id;
    podStruct.productId = jod.productId;
    podStruct.origProductId = jod.productId;
    podStruct.poDetailTypeId = 'ORDER';
    podStruct.orderQty = 1;
    podStruct.crtnTs = now;

    this.uow.createEntity(PoDetail, podStruct);

    if (isNewPo) {
      summary.push(
        `A new unposted Purchase Order has been created for <b>${manuf.name}</b> to replace the ${this.itemReplacementType.name.toLowerCase()} item.`,
      );
    } else {
      summary.push(
        `A new order detail has been added to existing unposted PO <b>${poHeader.id}</b> for <b>${manuf.name}</b> to replace the ${this.itemReplacementType.name.toLowerCase()} item.`,
      );
    }
  }

  cancel() {
    this.dialogRef.close(null);
  }
}
